import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  SwitchFieldProps
} from '@/components/form-builder/form-builder.types';
import { DacScenariosRepository } from '@/store/dac-scenarios-state/dac-scenarios-state.repository';
import {map} from 'rxjs';

interface IGetStepFormObjectProps {
  store: DacScenariosRepository;
  stepOptions?: SelectOption[];
  dossierOptions: SelectOption[];
  existingEtapeMap?: Map<string, boolean>;
  onTypeDossierChange?: (selected: SelectOption) => void;
}

export function stepFormObject({ store, stepOptions, dossierOptions, existingEtapeMap, onTypeDossierChange }: IGetStepFormObjectProps): DynamicForm {
  if (store.getNested('stepDto', 'isPublic') === undefined) {
    store.setNested('stepDto', 'isPublic', true, true);
  }
  if (store.getNested('stepDto', 'isEliminatoire') === undefined) {
    store.setNested('stepDto', 'isEliminatoire', false, true);
  }

  const stepField: DynamicField<SelectFieldProps> = {
    label: 'settings.dacScenarios.form.step.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable('stepSelected'),
      placeholder: 'settings.dacScenarios.form.step.placeholder',
      options: stepOptions,
      onSelectChange: (value: SelectOption) => {
        store.set('stepSelected', value);
        store.setNested('stepDto', 'etapeId', value.code);

        const stepCode = (value as any).stepCode as string;

        if (existingEtapeMap && existingEtapeMap.has(stepCode)) {
          // Etape already saved — set isPublic to match the saved value
          const savedIsPublic = existingEtapeMap.get(stepCode) ?? true;
          store.setNested('stepDto', 'isPublic', savedIsPublic, true);
        } else if (stepCode === 'ETAPE_3' || stepCode === 'ETAPE_UNIQUE') {
          // First time adding ETAPE_3/UNIQUE — default to true
          store.setNested('stepDto', 'isPublic', true, true);
        }
      },
    },
  };

  const typeDossierField: DynamicField<SelectFieldProps> = {
    label: 'settings.dacScenarios.form.typeDossier.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      placeholder: 'settings.dacScenarios.form.typeDossier.placeholder',
      options: dossierOptions,
      value: store.getObservable('dossierSelected'),
      onSelectChange: (selected: SelectOption) => {
        store.setNested('stepDto', 'typeDossierId', selected.code);
        store.set('dossierSelected', selected);
        onTypeDossierChange?.(selected);
      },
    },
  };

  const modeEvaluationField: DynamicField<SelectFieldProps> = {
    label: 'settings.dacScenarios.form.modeEvaluation.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: store.getObservable('dossierSelected').pipe(map(selected => !selected)),
    props: {
      placeholder: 'settings.dacScenarios.form.modeEvaluation.placeholder',
      options: store.getObservable('modeEvaluationOptions'),
      value: store.getObservable('modeEvaluationSelected'),
      onSelectChange: (selected: SelectOption) => {
        store.setNested('stepDto', 'modeEvaluationId', selected.code);
        store.set('modeEvaluationSelected', selected);

        if (selected.name !== 'NOTATION' && selected.name !== 'HYBRIDE') {
          store.setNested('stepDto', 'hasSeuil', null, true);
          store.setNested('stepDto', 'hasPonderation', null, true);
        } else {
          if (store.getNested('stepDto', 'hasSeuil') == null) {
            store.setNested('stepDto', 'hasSeuil', false, true);
          }
          if (store.getNested('stepDto', 'hasPonderation') == null) {
            store.setNested('stepDto', 'hasPonderation', false, true);
          }
        }
      },
    },
  };

  const orderEvaluationField: DynamicField<SelectFieldProps> = {
    label: 'settings.dacScenarios.form.orderEvaluation.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable('orderEvaluation'),
      placeholder: 'settings.dacScenarios.form.orderEvaluation.placeholder',
      options: [
        { name: '1', code: 1 },
        { name: '2', code: 2 },
        { name: '3', code: 3 },
      ],
      onSelectChange: (value: SelectOption) => {
        store.setNested('stepDto', 'orderEvaluation', value.code);
        store.set('orderEvaluation', value);
      },
    },
  };

  // isPublic is locked (disabled) when the selected etape already exists in the scenario
  const isPublicDisabled$ = store.getObservable<SelectOption>('stepSelected').pipe(
    map(selected => {
      if (!selected || !existingEtapeMap) return false;
      const stepCode = (selected as any).stepCode as string;
      return !!stepCode && existingEtapeMap.has(stepCode);
    })
  );

  const isPublic: DynamicField<SwitchFieldProps> = {
    label: 'settings.dacScenarios.form.public.label',
    variant: FieldVariant.SWITCH,
    isDisabled: isPublicDisabled$,

    props: {
      checked: store
        .getNestedObservable<boolean>('stepDto', 'isPublic')
        .pipe(
          map((value: boolean) => !!value)
        ),

      onChange: (event: any) => {
        store.setNested('stepDto', 'isPublic', event.checked, true);
      },
    },
  };
  const isEliminatoire: DynamicField<SwitchFieldProps> = {
    label: 'settings.dacScenarios.form.eliminatoire.label',
    variant: FieldVariant.SWITCH,
    props: {
      checked: store
        .getNestedObservable<boolean>('stepDto', 'isEliminatoire')
        .pipe(
          map((value: boolean) => value)
        ),
      onChange: (event: any) => {
        store.setNested('stepDto', 'isEliminatoire', event.checked, true);
      },
    },
  };

  const hasSeuil: DynamicField<SwitchFieldProps> = {
    label: 'settings.dacScenarios.form.hasSeuil.label',
    variant: FieldVariant.SWITCH,
    isHidden: store.getObservable('modeEvaluationSelected').pipe(
      map((selected) => !((selected as SelectOption)?.name === 'NOTATION' || (selected as SelectOption)?.name === 'HYBRIDE'))
    ),
    props: {
      checked: store
        .getNestedObservable<boolean | null | undefined>('stepDto', 'hasSeuil')
        .pipe(map((value) => !!value)),
      onChange: (event: any) => {
        store.setNested('stepDto', 'hasSeuil', event.checked, true);
      },
    },
  };

  const hasPonderation: DynamicField<SwitchFieldProps> = {
    label: 'settings.dacScenarios.form.hasPonderation.label',
    variant: FieldVariant.SWITCH,
    isHidden: store.getObservable('modeEvaluationSelected').pipe(
      map((selected) => !((selected as SelectOption)?.name === 'NOTATION' || (selected as SelectOption)?.name === 'HYBRIDE'))
    ),
    props: {
      checked: store
        .getNestedObservable<boolean | null | undefined>('stepDto', 'hasPonderation')
        .pipe(map((value) => !!value)),
      onChange: (event: any) => {
        store.setNested('stepDto', 'hasPonderation', event.checked, true);
      },
    },
  };

  return {
    title: 'settings.dacScenarios.form.step.title',
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [stepField],
          },
          {
            fields: [typeDossierField],
          },
          {
            fields: [modeEvaluationField],
          },
          {
            fields: [orderEvaluationField],
          },
          {
            fields: [isPublic,isEliminatoire],
          },
       
          {
            fields: [hasSeuil,hasPonderation],
          },

        ],
      },
    ],
  };
}
